import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
import {
  Heart,
  Shield,
  Users,
  Clock,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Activity,
  Thermometer,
  Droplet,
  Wind,
  Apple,
  Stethoscope,
} from "lucide-react";

export default function GeneralMedicinePage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("General Medicine");


  const subspecialties = [
    {
      title: "Diabetes & Endocrine Care",
      description: "Evaluation and management of diabetes, thyroid & metabolic disorders.",
      icon: Droplet,
      image: "/Diabetes&EndocrineCare.jpg",
    },
    {
      title: "Hypertension & Cardio-Metabolic Care",
      description: "Long-term control of high blood pressure and related risks.",
      icon: Heart,
      image: "/Hypertension&Cardio-MetabolicCare.jpg",
    },
    {
      title: "Respiratory Disorders",
      description: "Asthma, COPD, pneumonia & seasonal infections.",
      icon: Wind,
      image: "/RespiratoryDisorders.jpg",
    },
    {
      title: "Gastrointestinal Disorders",
      description: "Acidity, gastritis, IBS, liver infections & digestive issues.",
      icon: Activity,
      image: "/GastrointestinalDisorders.jpg",
    },
    {
      title: "Infectious Diseases",
      description: "Viral fevers, dengue, typhoid, flu, UTIs & more.",
      icon: Thermometer,
      image: "/InfectiousDiseases.jpg",
    },
    {
      title: "Preventive & Lifestyle Medicine",
      description: "Health checkups and wellness plans.",
      icon: Apple,
      image: "/Preventive&LifestyleMedicine.jpg",
    },
  ];

  const treatments = [
    "Treatment for Fever, Viral Infections & Seasonal Illnesses",
    "Diabetes & Thyroid Management Plans",
    "Hypertension & Cholesterol Control",
    "Respiratory Illness Treatment (Asthma, COPD, infections)",
    "Digestive & Liver Problem Management",
    "Lifestyle Disorder Counseling",
    "Annual & Corporate Health Checkups",
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Skilled & Experienced Physicians",
      description: "Expert medical care from highly qualified doctors with years of experience.",
    },
    {
      icon: Stethoscope,
      title: "Comprehensive Diagnostic Support",
      description: "Complete labs, imaging, ECG and advanced diagnostic facilities on-site.",
    },
    {
      icon: Heart,
      title: "Personalized Chronic Disease Management",
      description: "Tailored treatment plans for long-term conditions like diabetes and hypertension.",
    },
    {
      icon: Shield,
      title: "Integrated Specialist Care",
      description: "Seamless coordination with specialists when advanced care is needed.",
    },
    {
      icon: Clock,
      title: "24x7 Emergency & Acute Care",
      description: "Round-the-clock emergency services for urgent medical needs.",
    },
  ];


  const articles = [
    {
      title: "How to Identify Common Viral Infections Early",
      description: "Learn the warning signs and symptoms of common viral infections.",
      link: "#",
    },
    {
      title: "Lifestyle Habits for Managing Diabetes Naturally",
      description: "Practical tips for controlling blood sugar through diet and exercise.",
      link: "#",
    },
    {
      title: "How High Blood Pressure Affects Your Health",
      description: "Understanding the risks and complications of uncontrolled hypertension.",
      link: "#",
    },
    {
      title: "Tips to Boost Immunity Against Seasonal Illnesses",
      description: "Strengthen your immune system with proven health strategies.",
      link: "#",
    },
  ];

  const faqs = [
    {
      question: "What conditions do general medicine doctors treat?",
      answer:
        "All common medical illnesses including fever, diabetes, hypertension, infections, thyroid issues, and digestive disorders.",
    },
    {
      question: "Do you offer preventive health checkups?",
      answer: "Yes, MAMS Hospitals provides complete annual and specialized health check packages.",
    },
    {
      question: "Can I consult a physician for long-term diabetes or BP control?",
      answer: "Absolutely — we offer structured follow-up and personalized management plans.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals General Medicine Department",
    "url": "https://mamshospitals.com/general-medicine",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/GeneralMedicine.jpg",
    "description": "MAMS Hospitals provides advanced general medicine treatment in Hyderabad including diabetes management, hypertension care, respiratory disease treatment, infectious disease care, and preventive health services.",
    "medicalSpecialty": "General Medicine",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Diabetes Management"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Hypertension Treatment"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Respiratory Disease Treatment"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Preventive Health Checkups"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Infectious Disease Treatment"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What conditions do general medicine doctors treat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "General medicine doctors treat diabetes, hypertension, infections, fever, thyroid disorders, respiratory conditions, and digestive problems."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide preventive health checkups?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers preventive health checkups and annual wellness packages."
        }
      },
      {
        "@type": "Question",
        "name": "Can I consult for long-term diabetes and BP management?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our physicians provide personalized long-term management for diabetes, blood pressure, and chronic diseases."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "General Medicine Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/general-medicine",
    "description": "Best general medicine hospital in Hyderabad offering diabetes care, hypertension treatment, infectious disease care, preventive health checkups, and respiratory care.",
    "keywords": [
      "General Medicine Hospital Hyderabad",
      "Best Physician Hyderabad",
      "Diabetes Treatment Hyderabad",
      "Hypertension Treatment Hyderabad",
      "Fever Treatment Hyderabad",
      "Preventive Health Checkup Hyderabad",
      "General Medicine Hospital Bachupally"
    ]
  }
];
  return (
    <>
    <SEO
      title="Best General Medicine Hospital in Hyderabad | Expert Physicians | MAMS Hospitals"
      description="MAMS Hospitals offers advanced general medicine treatment in Hyderabad including diabetes care, hypertension treatment, respiratory care, preventive health checkups, and infectious disease management."
      keywords="general medicine hospital Hyderabad,
    best physician Hyderabad,
    diabetes treatment Hyderabad,
    hypertension treatment Hyderabad,
    fever treatment Hyderabad,
    infectious disease treatment Hyderabad,
    preventive health checkup Hyderabad,
    thyroid treatment Hyderabad,
    respiratory disease treatment Hyderabad,
    general physician Bachupally,
    internal medicine Hyderabad,
    chronic disease management Hyderabad,
    MAMS Hospitals General Medicine"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-teal-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Your First Point of Contact
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                General Medicine at MAMS Hospitals
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Comprehensive diagnosis, treatment, and long-term management of various medical conditions. Trusted
                medical care for every age.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative mt-8 md:mt-0">
              <img
                src="/GeneralMedicine.jpg"
                alt="General Medicine Care"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/general-medicine"
      />
              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="bg-teal-100 p-3 rounded-full">
                    <Heart className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">24/7</div>
                    <div className="text-sm text-gray-600">Emergency Care</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-Specialties Section */}
      <section className="py-16 md:py-20 px-0 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sub-Specialties in General Medicine
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive medical care across multiple specializations for complete health management.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => {
              const IconComponent = specialty.icon;

              return (
                <Card
                  key={index}
                  className="group hover:shadow-xl transition-all duration-300 border-gray-200 overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={specialty.image || "/placeholder.svg"}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white">{specialty.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gray-600">{specialty.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Treatments & Procedures */}
      <section className="py-16 md:py-20 px-0 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Procedures</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Advanced medical treatments and diagnostic services for comprehensive healthcare.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {treatments.map((treatment, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700">{treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 md:py-20 px-0 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MAMS Hospitals for General Medicine
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience exceptional medical care with our comprehensive approach to healthcare.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const IconComponent = item.icon;

              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* <div className="mt-8 bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-8 text-white text-center">
            <p className="text-lg font-semibold">Easily accessible location near Miyapur & Bachupally</p>
          </div> */}
        </div>
      </section>

      {/* Specialist Section */}
                            {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* Recent Articles */}
      {/* <section className="py-16 md:py-20 px-0 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Recent Articles</h2>
            <p className="text-lg text-gray-600">
              Stay informed with the latest health insights and medical updates
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {articles.map((article, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-gray-200 cursor-pointer"
              >
                <CardContent className="p-6">
                  <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                    <Activity className="h-6 w-6 text-teal-600 group-hover:text-white transition-colors" />
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors">
                    {article.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">{article.description}</p>

                  <Link to={article.link} className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
                    Read More →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQs Section */}
      <section className="py-16 md:py-20 px-0 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Find answers to common questions about general medicine care</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
